import React from "react";
import { TextField } from "@mui/material";
import {
  assembledStreamAddress,
  hasExplicitPort,
  parseStreamUrl,
  streamAddressProblems,
  streamUrlHints,
} from "./streamAddress";

/**
 * How the pasted URL was split, shown before (while typing) and after it
 * fills the fields (ENG-2042), so a wrong split is visible instead of saved.
 * The password is masked; only its length shows.
 */
const ParsePreview = ({ raw }: { raw: string }) => {
  const parsed = parseStreamUrl(raw);
  const hints = streamUrlHints(raw);
  if (!parsed && hints.length === 0) return null;

  const rows: [string, string][] = parsed
    ? [
        ["Protocol", parsed.protocol.toUpperCase()],
        ["Host", parsed.host],
        [
          "Port",
          hasExplicitPort(raw) ? parsed.port : `${parsed.port} (default)`,
        ],
        ["Username", parsed.username || "(none)"],
        [
          "Password",
          parsed.password
            ? `•••• (${parsed.password.length} characters)`
            : "(none)",
        ],
        ["Stream Parameter", parsed.path || "(none)"],
      ]
    : [];

  return (
    <div
      className="font-mulish text-[13px] text-[#283E6D] mt-2 max-w-[350px]"
      data-testid="stream-url-parse-preview"
    >
      {parsed && (
        <>
          <p>How we read this URL:</p>
          <ul className="ml-4">
            {rows.map(([label, value]) => (
              <li key={label} data-testid={`stream-url-parsed-${label}`}>
                {label}: <span className="font-bold break-all">{value}</span>
              </li>
            ))}
          </ul>
        </>
      )}
      {hints.map((hint) => (
        <p
          key={hint}
          className="text-[#B26A00] mt-1"
          data-testid="stream-url-hint"
        >
          {hint}
        </p>
      ))}
    </div>
  );
};

/**
 * Shown under a camera step that asks for Host + Stream Parameter (ENG-1924):
 * a "paste the full URL" box that fills the fields, the address the connector
 * will dial, and field-named problems that also block Submit
 * (handleCheckForRequriedFields).
 */
export const StreamUrlAssist = ({ formik }: { formik: any }) => {
  const [pasted, setPasted] = React.useState("");
  const [note, setNote] = React.useState<string | null>(null);
  // The last URL applied to the fields, so its preview stays after the box
  // clears.
  const [applied, setApplied] = React.useState("");

  const values = formik.values || {};
  const problems = streamAddressProblems(values);
  const address = assembledStreamAddress(values);

  const setField = (field: string, value: string) => {
    if (typeof formik.setFieldValue === "function") {
      formik.setFieldValue(field, value, false);
    } else {
      formik.values[field] = value;
    }
  };

  const applyUrl = (raw: string) => {
    const parsed = parseStreamUrl(raw);
    if (!parsed) {
      setNote(
        raw.trim()
          ? "That doesn't look like a stream URL. Example: rtsp://admin:password@203.0.113.20:554/Streaming/Channels/101"
          : null
      );
      return;
    }
    setField("ip", parsed.host);
    if (parsed.protocol.startsWith("http") && "http" in values) {
      setField("http", parsed.port);
    } else if ("rtsp" in values || parsed.protocol.startsWith("rtsp")) {
      setField("rtsp", parsed.port);
    }
    setField("channel", parsed.path);
    if (parsed.username && "username" in values) {
      setField("username", parsed.username);
    }
    if (parsed.password && "password" in values) {
      setField("password", parsed.password);
    }
    setApplied(raw);
    setPasted("");
    setNote(
      `Filled Host, port and Stream Parameter from the URL. This URL uses ${parsed.protocol.toUpperCase()}: the site's Streaming Protocol must be ${parsed.protocol.toUpperCase()} too.`
    );
  };

  return (
    <div data-testid="stream-url-assist" className="mt-2">
      <TextField
        id="stream-url-paste"
        label="Have a working stream URL? Paste it here"
        placeholder="rtsp://admin:password@203.0.113.20:554/Streaming/Channels/101"
        variant="standard"
        autoComplete="off"
        sx={{ width: 350 }}
        value={pasted}
        onChange={(event) => {
          const nextValue = event.target.value;
          setPasted(nextValue);
        }}
        onBlur={() => pasted && applyUrl(pasted)}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            applyUrl(pasted);
          }
        }}
        helperText={
          <>
            Example:
            rtsp://admin:password@203.0.113.20:554/Streaming/Channels/101
            <br />
            We fill Host, port and Stream Parameter from it, and Username /
            Password when the URL includes them. The login and the port are
            optional. Use the address Actuate can reach: the public IP or
            hostname, or the camera&apos;s private IP only when this site
            connects over a VPN. If the password contains &quot;/&quot;, write
            it as %2F. An RTSPS URL without a port uses 554; Genetec Media
            Gateway usually needs :654.
          </>
        }
      />
      <ParsePreview raw={pasted || applied} />
      {note && (
        <p
          className="font-mulish text-[13px] text-[#283E6D] mt-1"
          data-testid="stream-url-note"
        >
          {note}
        </p>
      )}
      {address && (
        <p
          className="font-mulish text-[13px] text-[#283E6D] mt-2 max-w-[350px]"
          data-testid="stream-address-preview"
        >
          We will connect to:{" "}
          {/* A stream address has no spaces to wrap at: let it break anywhere.
              The wizard box is right-anchored with no width of its own
              (WizardCameraScreen), so it sizes to its widest line; the
              max-w keeps a long host or path from widening it and shifting
              the form left (SUP-807). Matches the fields' 350px. */}
          <span
            className="font-bold break-all"
            data-testid="stream-address-preview-value"
          >
            {address}
          </span>
        </p>
      )}
      {Object.entries(problems).map(([field, message]) => (
        <p
          key={field}
          className="font-mulish text-[13px] text-[#D32F2F] mt-1"
          data-testid={`stream-address-problem-${field}`}
        >
          {message}
        </p>
      ))}
    </div>
  );
};
